import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

const fmt = (n) => n ? '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—';
const val = (v) => v || '—';
const check = (v) => v ? '☑' : '☐';

export default function ContractorPackets() {
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');
  const printRef = useRef();

  useEffect(() => { loadPackets(); }, []);

  async function loadPackets() {
    try {
      const res = await api.get('/contractor-packets');
      setPackets(Array.isArray(res.data) ? res.data : []);
    } catch {} finally { setLoading(false); }
  }

  function handlePrint() {
    const content = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>CPR Contractor Packet</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #000; }
        h1 { font-size: 16px; text-align: center; margin-bottom: 2px; }
        h2 { font-size: 13px; margin: 14px 0 6px; border-bottom: 1px solid #000; padding-bottom: 2px; }
        .header { text-align: center; border-bottom: 2px solid #000; margin-bottom: 14px; padding-bottom: 8px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 20px; }
        .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0 20px; }
        .field { margin-bottom: 6px; display: flex; gap: 6px; }
        .label { color: #555; min-width: 120px; flex-shrink: 0; }
        .value { font-weight: 600; border-bottom: 1px solid #999; flex: 1; min-width: 80px; }
        .checkbox { margin-right: 6px; }
        .section { margin-bottom: 16px; }
        .sig-line { border-bottom: 1px solid #000; margin-top: 20px; width: 60%; display: inline-block; }
        .total { font-size: 13px; font-weight: 700; }
        @media print { body { margin: 10mm; } }
      </style></head><body>${content}</body></html>
    `);
    win.document.close();
    win.print();
  }

  const filtered = packets.filter(p =>
    !filter ||
    p.contractor_name?.toLowerCase().includes(filter.toLowerCase()) ||
    p.po_number?.toLowerCase().includes(filter.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(filter.toLowerCase())
  );

  const grouped = filtered.reduce((acc, p) => {
    const key = p.po_number;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <>
      <div className="page-header">
        <h2>Contractor Packets</h2>
        <div className="page-header-actions">
          <input className="form-input" placeholder="Search by contractor, PO#, customer..." value={filter}
            onChange={e => setFilter(e.target.value)} style={{ width: 280 }} />
          {selected && (
            <button className="btn btn-outline btn-sm" onClick={handlePrint}>🖨️ Print Packet</button>
          )}
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="empty-state"><p>No contractor packets submitted yet.</p></div>
        ) : (
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* Left — job list */}
            <div style={{ width: 300, flexShrink: 0 }}>
              {Object.entries(grouped).map(([po, jobPackets]) => {
                const latest = jobPackets[jobPackets.length - 1];
                return (
                  <div key={po} style={{ background: '#fff', borderRadius: 8, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <div style={{ padding: '12px 14px', background: selected && jobPackets.find(p => p.id === selected.id) ? '#0D3B2E' : '#f9f9f9', cursor: 'pointer' }}
                      onClick={() => setSelected(jobPackets[jobPackets.length - 1])}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: selected && jobPackets.find(p => p.id === selected.id) ? '#F57F17' : '#0D3B2E' }}>
                        {latest.customer_name || po}
                      </div>
                      <div style={{ fontSize: 11, color: selected && jobPackets.find(p => p.id === selected.id) ? '#ccc' : '#888', marginTop: 2 }}>
                        {po} · {latest.contractor_name}
                      </div>
                      <div style={{ fontSize: 11, color: selected && jobPackets.find(p => p.id === selected.id) ? '#aaa' : '#aaa', marginTop: 4 }}>
                        {jobPackets.length}/2 submissions
                      </div>
                    </div>
                    {jobPackets.length > 1 && (
                      <div style={{ display: 'flex', borderTop: '1px solid #eee' }}>
                        {jobPackets.map(p => (
                          <button key={p.id} onClick={() => setSelected(p)}
                            style={{ flex: 1, padding: '6px', border: 'none', fontSize: 11, cursor: 'pointer', fontWeight: selected?.id === p.id ? 700 : 400,
                              background: selected?.id === p.id ? '#e8f5e9' : '#fff', color: selected?.id === p.id ? '#1B5E20' : '#555' }}>
                            #{p.submission_number}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right — packet form view */}
            {selected ? (
              <div style={{ flex: 1, minWidth: 500 }}>
                <div ref={printRef} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '28px 32px', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                  
                  {/* Header */}
                  <div className="header" style={{ textAlign: 'center', borderBottom: '2px solid #0D3B2E', marginBottom: 20, paddingBottom: 12 }}>
                    <h1 style={{ fontFamily: 'serif', fontSize: 22, color: '#0D3B2E', margin: '0 0 2px' }}>CPR HOME SOLUTIONS</h1>
                    <div style={{ fontSize: 13, color: '#555' }}>MASTER CONTRACTOR PACKET</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Phone: (562) 509-0232</div>
                  </div>

                  {/* Job info bar */}
                  <div style={{ background: '#e8f5e9', borderRadius: 6, padding: '10px 14px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: 12 }}>
                    <Field label="Project ID" value={selected.po_number} />
                    <Field label="Date" value={selected.created_at?.slice(0,10)} />
                    <Field label="Contractor" value={selected.contractor_name} />
                    <Field label="Submission" value={`#${selected.submission_number} of 2`} />
                  </div>

                  {/* Section 1 */}
                  <Section num="1" title="Contractor Agreement & Expectations">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        [selected.agreed_standards, 'I agree to follow CPR Home Solutions installation standards'],
                        [selected.agreed_materials, 'I agree all materials must be listed prior to project start'],
                        [selected.agreed_license, 'I agree to maintain active license and insurance'],
                        [selected.agreed_cleanliness, 'I agree to maintain jobsite cleanliness and professionalism'],
                      ].map(([checked, label], i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                          <span style={{ fontSize: 16, color: checked ? '#1B5E20' : '#ccc' }}>{check(checked)}</span>
                          <span style={{ color: checked ? '#333' : '#aaa' }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', marginTop: 14 }}>
                      <SigField label="Contractor Name" value={selected.contractor_name} />
                      <SigField label="Date" value={selected.created_at?.slice(0,10)} />
                    </div>
                  </Section>

                  {/* Section 2 */}
                  <Section num="2" title="Bid Request Summary">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 20px' }}>
                      <Field label="Project ID" value={selected.po_number} />
                      <Field label="Date" value={selected.created_at?.slice(0,10)} />
                      <Field label="Homeowner" value={selected.customer_name} />
                      <Field label="Address" value={selected.address} />
                      <Field label="Stories" value={val(selected.stories)} />
                      <Field label="Access" value={val(selected.access_level)} />
                      <Field label="Tear-Off Layers" value={val(selected.tearoff_layers)} />
                      <Field label="Start Requested" value={val(selected.start_requested)} />
                      <Field label="Bid Due" value={val(selected.bid_due)} />
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <Field label="Scope" value={val(selected.scope)} />
                    </div>
                  </Section>

                  {/* Section 3 */}
                  <Section num="3" title="Bid Submission">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 20px' }}>
                      <Field label="Labor" value={fmt(selected.labor_cost)} />
                      <Field label="Materials" value={fmt(selected.materials_cost)} />
                      <Field label="Dump Fees" value={fmt(selected.dump_fees)} />
                      <Field label="Permit Fees" value={fmt(selected.permit_fees)} />
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#f5f5f5', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#0D3B2E' }}>TOTAL BID</span>
                      <span style={{ fontWeight: 700, fontSize: 18, color: '#0D3B2E' }}>{fmt(selected.total_bid)}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 20px', marginTop: 10 }}>
                      <Field label="Start Date" value={val(selected.start_date)} />
                      <Field label="Duration (Days)" value={val(selected.duration_days)} />
                      <Field label="Crew Size" value={val(selected.crew_size)} />
                      <Field label="Warranty" value={val(selected.warranty)} />
                    </div>
                  </Section>

                  {/* Section 4 */}
                  <Section num="4" title="Material Order – Roofing System">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 20px' }}>
                      <Field label="Squares" value={val(selected.squares)} />
                      <Field label="Shingle (Owens Corning)" value={val(selected.shingle_brand)} />
                      <Field label="Color" value={val(selected.shingle_color)} />
                      <Field label="Underlayment" value={val(selected.underlayment)} />
                      <Field label="Starter Bundles" value={val(selected.starter_bundles)} />
                      <Field label="Hip/Ridge Bundles" value={val(selected.hip_ridge_bundles)} />
                      <Field label="Ridge Vent (LF)" value={val(selected.ridge_vent_lf)} />
                      <Field label="Vents (qty)" value={val(selected.vents_qty)} />
                      <Field label="Drip Edge (LF)" value={val(selected.drip_edge_lf)} />
                      <Field label="Flashing" value={val(selected.flashing)} />
                      <Field label="OSB Sheets" value={val(selected.osb_sheets)} />
                      <Field label="Fascia" value={val(selected.fascia)} />
                      <Field label="Dumpsters" value={val(selected.dumpsters)} />
                      <Field label="Tear-Off Layers" value={val(selected.tearoff_layers)} />
                    </div>
                  </Section>

                  {/* Section 5 */}
                  <Section num="5" title="Additional Materials (If Applicable)">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 20px' }}>
                      <Field label="Paint Gallons" value={val(selected.paint_gallons)} />
                      <Field label="Primer" value={val(selected.primer)} />
                      <Field label="Repairs Needed" value={val(selected.repairs_needed)} />
                      <Field label="Windows Qty" value={val(selected.windows_qty)} />
                      <Field label="Window Type" value={val(selected.windows_type)} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, color: selected.sizes_attached ? '#1B5E20' : '#ccc' }}>{check(selected.sizes_attached)}</span>
                        <span style={{ fontSize: 12 }}>Sizes Attached</span>
                      </div>
                    </div>
                  </Section>

                  {/* Section 6 */}
                  <Section num="6" title="Scheduling & Delivery">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 20px' }}>
                      <Field label="Material Delivery Date" value={val(selected.delivery_date)} />
                      <Field label="Start Date Confirmed" value={val(selected.start_confirmed)} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, color: selected.inspection_required ? '#1B5E20' : '#ccc' }}>{check(selected.inspection_required)}</span>
                        <span style={{ fontSize: 12 }}>Inspection Required</span>
                      </div>
                    </div>
                    {selected.notes && (
                      <div style={{ marginTop: 10 }}>
                        <Field label="Notes" value={selected.notes} />
                      </div>
                    )}
                  </Section>

                  {/* Section 7 */}
                  <Section num="7" title="Final Contractor Confirmation">
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 14, fontStyle: 'italic' }}>
                      I confirm all information provided is accurate and complete. I understand missing items may delay project and are my responsibility.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
                      <SigField label="Contractor Name" value={selected.contractor_name} />
                      <SigField label="Date" value={selected.created_at?.slice(0,10)} />
                    </div>
                  </Section>

                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 14, minHeight: 200 }}>
                Select a job from the left to view the packet
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Section({ num, title, children }) {
  return (
    <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#0D3B2E', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ background: '#0D3B2E', color: '#F57F17', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{num}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: 13, borderBottom: '1px solid #ddd', paddingBottom: 2, minHeight: 18 }}>{value || '—'}</div>
    </div>
  );
}

function SigField({ label, value }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ borderBottom: '1px solid #333', paddingBottom: 4, minHeight: 24, fontWeight: 600, fontSize: 13 }}>{value || ''}</div>
      <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>{label}</div>
    </div>
  );
}
